<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema } from '@bagelink/vue'
const schema = defineSchema({})
export const BlockCriticismFaith = defineBlock({
	thumbnail: '/block-thumbs/BlockCriticismFaith.svg',
	category: 'Content',
	order: 200,
	name: 'BlockCriticismFaith',
	label: 'Criticism & Faith',
	icon: 'balance',
	schema,
	dataSource: { collection: 'subcategories', store: 'the_torah', label: 'Homepage Topics' },
})
</script>
<script setup lang="ts">
import { useBloxRuntime } from '@bagelink/blox'
import { Btn, Icon, Skeleton, Swiper } from '@bagelink/vue'
import { computed } from 'vue'
import BlockWidgets from '@/components/blocks/BlockWidgets.vue'
import { useCollection } from '@/composables/useDatastore'



interface CategoryRecord {
	_id?: string
	slug?: string
	topicGroupIds?: string[]
	featuredArticleIds?: string[]
}

interface SubcategoryRecord {
	_id?: string
	name?: string
	slug?: string
	parentCategoryId?: string
	showOnHomepage?: boolean
	articleIds?: string[]
}

interface HomepageTopic {
	name: string
	link: string
	parentCategoryId?: string
	parentSlug?: string
	articleIds?: string[]
}

const PREVIEW_TOPICS: HomepageTopic[] = [
	{ name: 'Maimonides', link: '/categories/modern-faith#maimonides' },
	{ name: 'Torah From Heaven', link: '/categories/modern-faith#torah-from-heaven' },
	{ name: 'Biblical Archaeology', link: '/categories/bible-criticism#biblical-archaeology' },
]
const PREVIEW_ARTICLES = [
	{
		title: 'Faith and Critical Study',
		authors: [{ name: 'Sample Author', title: 'Dr.', image: '/webclip.png' }],
		image: '/og.jpg',
		category: 'Modern Faith',
		link: '/',
	},
	{
		title: 'How the Torah Was Composed',
		authors: [{ name: 'Sample Scholar', title: 'Prof.', image: '/webclip.png' }],
		image: '/og.jpg',
		category: 'Biblical Criticism',
		link: '/',
	},
]

const { isPreview } = useBloxRuntime()
const { items: datastoreCategories, loading: categoriesLoading } = useCollection<CategoryRecord>(
	'categories',
	{
		limit: 50,
		pick: ['id', '_id', 'name', 'slug', 'topicGroupIds', '_ssgFeaturedArticles'],
	},
)
const { items: datastoreSubcategories, loading: subcategoriesLoading } =
	useCollection<SubcategoryRecord>('subcategories', {
		limit: 1000,
		pick: [
			'id',
			'_id',
			'name',
			'slug',
			'showOnHomepage',
			'parentCategoryId',
			'articleIds',
			'_ssgArticles',
		],
	})
const contentLoading = computed(() => categoriesLoading.value || subcategoriesLoading.value)

const visibleCategories = computed<HomepageTopic[]>(() => {
	const parentBySubcategoryId = new Map<string, CategoryRecord>()
	for (const category of datastoreCategories.value) {
		if (!category.slug) continue
		for (const subcategoryId of category.topicGroupIds ?? []) {
			parentBySubcategoryId.set(subcategoryId, category)
		}
	}

	const topics = datastoreSubcategories.value
		.filter(topic => topic.showOnHomepage && topic.name && topic.slug)
		.map((topic): HomepageTopic | null => {
			const parent = topic.parentCategoryId
				? datastoreCategories.value.find(category => category._id === topic.parentCategoryId)
				: topic._id
					? parentBySubcategoryId.get(topic._id)
					: undefined

			return parent?.slug
				? {
						name: topic.name!,
						link: `/categories/${parent.slug}#${topic.slug}`,
						parentCategoryId: parent._id,
						parentSlug: parent.slug,
						articleIds: topic.articleIds ?? [],
					}
				: null
		})
		.filter((topic): topic is HomepageTopic => !!topic)
	return topics.length || contentLoading.value || !isPreview.value ? topics : PREVIEW_TOPICS
})

const criticismFaithParents = computed(() =>
	datastoreCategories.value.filter(
		category => category.slug === 'bible-criticism' || category.slug === 'modern-faith',
	),
)

function belongsToCriticismFaith(topic: SubcategoryRecord) {
	return criticismFaithParents.value.some(
		category =>
			(topic.parentCategoryId && category._id === topic.parentCategoryId) ||
			(topic._id && category.topicGroupIds?.includes(topic._id)),
	)
}

const criticismFaithArticles = computed(() => {
	return datastoreSubcategories.value
		.filter(belongsToCriticismFaith)
		.flatMap(topic => (topic as any)._ssgArticles ?? [])
})

function toCard(article: any) {
	return {
		title: article.title || '',
		authors: article.authors || [],
		image: article.image || '',
		category: article.category || '',
		link: article.link || `/article/${article.slug}`,
	}
}

const widgetContent = computed(() => {
	const featured = criticismFaithParents.value.flatMap(
		category => (category as any)._ssgFeaturedArticles ?? [],
	)

	const newestFallback = [...criticismFaithArticles.value].sort(
		(a: any, b: any) =>
			new Date(b.publishDate || 0).getTime() - new Date(a.publishDate || 0).getTime(),
	)
	const popularFallback = [...criticismFaithArticles.value].sort(
		(a: any, b: any) => (b.hits || 0) - (a.hits || 0),
	)

	// The Featured tab must not repeat the Latest tab: when neither category has
	// curated `featuredArticleIds`, fall back to the next-newest pieces instead of
	// the same two the Latest tab shows.
	const latest = newestFallback.slice(0, 2)
	const latestIds = new Set(latest.map((article: any) => article._id))
	const featuredFallback = newestFallback.filter((article: any) => !latestIds.has(article._id))

	const content = {
		latest: latest.map(toCard),
		popular: popularFallback.slice(0, 2).map(toCard),
		featured: (featured.length ? featured : featuredFallback.length ? featuredFallback : newestFallback)
			.slice(0, 2)
			.map(toCard),
	}
	if (
		content.latest.length ||
		content.popular.length ||
		content.featured.length ||
		contentLoading.value ||
		!isPreview.value
	)
		return content
	return {
		latest: PREVIEW_ARTICLES,
		popular: PREVIEW_ARTICLES,
		featured: PREVIEW_ARTICLES,
	}
})
</script>

<template>
	<div
		v-if="
			contentLoading ||
			visibleCategories.length ||
			widgetContent.latest.length ||
			widgetContent.popular.length ||
			widgetContent.featured.length
		"
		class="w1100px pt-6 m_pt-2 m_pb-1 txt-center testMe1"
	>
		<template v-if="contentLoading">
			<Skeleton width="360px" height="30px" class="mx-auto mb-1" />
			<div class="flex-center gap-2 px-3">
				<Skeleton v-for="topic in 5" :key="topic" width="140px" height="22px" />
			</div>
			<BlockWidgets loading entity-variant="category" />
		</template>
		<template v-else>
			<h3 class="txt24 m-0 line-height-13 regular balance m_flex m_column">
				<router-link class="decoration-none" to="/categories/bible-criticism">
					Biblical Criticism
				</router-link>
				&
				<router-link class="decoration-none" to="/categories/modern-faith">
					Modern Faith
				</router-link>
			</h3>
			<Swiper
				v-if="visibleCategories.length"
				:items="visibleCategories"
				:loop="true"
				slides-per-view="auto"
				slide-width="max-content"
				:space-between="20"
				class="px-3 mt-1"
			>
				<template #default="{ item }">
					<Btn :to="(item as { name: string; link: string }).link" flat class="px-1">
						<i class="txt17 light redhover">{{ (item as { name: string; link: string }).name }}</i>
					</Btn>
				</template>
				<template #navigation="{ prev, next }">
					<Icon
						v-if="visibleCategories.length > 1"
						name="chevron_left"
						class="user-select-none hover z-99 absolute -top-025 color-primary start line-height-1"
						:size="3"
						:weight="200"
						@click="prev()"
					/>
					<Icon
						v-if="visibleCategories.length > 1"
						name="chevron_right"
						class="user-select-none hover z-99 absolute -top-025 color-primary end line-height-1"
						:size="3"
						:weight="200"
						@click="next()"
					/>
				</template>
			</Swiper>

			<BlockWidgets
				entity-variant="category"
				:latest-articles="widgetContent.latest"
				:popular-articles="widgetContent.popular"
				:featured-articles="widgetContent.featured"
			/>
		</template>
	</div>
</template>
